// React Components
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// Material UI Components
import { Grid, Paper } from "@mui/material";
import Link from "@mui/material/Link";
import Button from "@mui/material/Button";
import { Alert, AlertTitle } from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Audit Vault Components
import AccessDenied from "../components/common/AccessDenied";
import ServiceIsDown from "../components/common/ServiceIsDown";
import ProgressBar from "../components/common/ProgressBar";
import AuditGrowth from "../components/reports/AuditGrowth";
import CustomerPageScaffold from "../components/common/CustomerPageScaffold";
import FilterPanel from "../components/common/FilterPanel";
import PeriodDropDown from "../components/reports/PeriodDropDown";
import TeamsAllActivity from "../components/reports/TeamsAllActivity";
import ImageTeams from "../imgs/iconTeams.svg";
import TenantSetupRequired from "../components/tenant/TenantSetupRequired";
import InsightsSearchBox from "../components/reports/InsightsSearchBox";

// Audit Vault Utilities
import { getCompanyByCompanyId } from "../utilities/common-company";
import { NumberBadge } from "../utilities/common-ui-utils";
import {
    REPORT_PERIOD_LIST,
    REPORT_ON_PERIOD_30DAYS,
    REPORT_ON_PERIOD_90DAYS,
    REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE,
} from "../constants/constants-reportperiods";
import { formatAuditJobRunTimeDateServerTimeToGMT } from "../utilities/common-date-utils";
import { verifyUserHasTenantRolePermissions } from "../utilities/common-user-utils";
import { ROLE_CODE_TENANTREPORTREADER } from "../constants/constants-roles";
import { getHealthStatsByTenantId } from "../utilities/common-healthstats";

const theBreadcrumbPath = [
    { name: "Home", link: "/" },
    { name: "Reports", link: "/Reports/ReportsHome" },
    { name: "Teams Reports", link: "/Reports/teams" },
];

const theSectionTitle = "Teams Reports Dashboard";
const theSectionDesc =
    "Microsoft Teams audit logs enable you to capture and log activities to help organizations monitor, track, and ensure compliance with their communication and collaboration practices. These logs provide detailed insights into various activities within Teams including message, meeting, and user + admin related activities.";
const isAdminPage = false;

// Common styles for report links inside tab panels
const linkListSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
};

const reportLinkSx = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    whiteSpace: 'normal',
    marginBottom: 0.5,
};


function ReportsTeams(props) {
    const { user, openCompanyNag, setOpenCompanyNag } = props;

    // Component Constants
    const [company, setCompany] = useState("");
    const [tenantHealthStats, setTenantHealthStats] = useState("");
    const [focusTenantId, setFocusTenantId] = useState("");
    const [focusPeriodId, setFocusPeriodId] = useState(REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE);
    const [accessDenied, setAccessDenied] = useState(false);

    const [loading, setLoading] = useState(true);
    const [serviceIsDownError, setServiceIsDownError] = useState(false);
    const navigate = useNavigate();

    const [totalTeamsAuditRecords, setTotalTeamsAuditRecords] = useState(null);
    const [loadingTotalTeamsAuditRecords, setLoadingTotalTeamsAuditRecords] = useState(false);
    const [totalTeamsAuditRecordsLoaded, setTotalTeamsAuditRecordsLoaded] = useState(false);

    const [selectedTenantOption, setSelectedTenantOption] = useState("");
    const [selectedPeriodOption, setSelectedPeriodOption] = useState("");
    const [tabValue, setTabValue] = useState(0);

    // Component Functions
    async function fetchTenantHealthStats(theTenantId) {
        setLoading(true);
        try {
            const response2 = await getHealthStatsByTenantId(theTenantId, 5, user);
            if (response2) {
                setTenantHealthStats(response2);
            }
        } catch (e) {
            console.log("ERROR: ReportTeams.fetchTenantHealthStats");
            console.log(e);
            setServiceIsDownError(true);
        } finally {
            setLoading(false);
        }
    }

    // Load the total teams audit records on demand.
    async function fetchTotalAuditRecordsOnDemand() {
        if (!user?.tenantId || loadingTotalTeamsAuditRecords || totalTeamsAuditRecordsLoaded) {
            return;
        }

        setLoadingTotalTeamsAuditRecords(true);

        try {
            // Heavier on-demand total counts load.
            const response = await getHealthStatsByTenantId(user.tenantId, 15, user);            
            if (response) {
                const teamsTotal = response.totalTeamsAuditRecords;
                setTotalTeamsAuditRecords(teamsTotal);
                setTotalTeamsAuditRecordsLoaded(true);                
            }
        }
        catch (e) {
            console.log("ERROR: Report.fetchTotalAuditRecordsOnDemand");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally {
            setLoadingTotalTeamsAuditRecords(false);
        }
    }


    useEffect(() => {
        async function fetchCompanyData() {
            try {

                if (!verifyUserHasTenantRolePermissions(user, ROLE_CODE_TENANTREPORTREADER)) {
                    setAccessDenied(true);
                    return;
                }

                if (user && user.companyId) {
                    const response1 = await getCompanyByCompanyId(user.companyId, setServiceIsDownError);
                    if (response1) {
                        setCompany(response1);

                        if (response1.tenantList && response1.tenantList.length > 0) {
                            const defaultTenant = response1.tenantList[0];
                            setSelectedTenantOption(defaultTenant);
                            fetchTenantHealthStats(defaultTenant.id);
                            setFocusTenantId(defaultTenant.id);
                        }

                        // Set default period option.
                        setSelectedPeriodOption(REPORT_PERIOD_LIST[REPORT_ON_PERIOD_DEFAULT_DROPDOWNVALUE]);
                    }
                }
                setLoading(false);
            } catch (e) {
                console.log("ERROR: ReportTeams.fetchCompanyData");
                console.log(e);
                setServiceIsDownError(true);
            } finally {
                setLoading(false);
            }
        }

        fetchCompanyData();
    }, []);

    function TabPanel(props) {
        const { children, value, index, ...other } = props;

        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box sx={{ p: 3, textAlign: "left" }}>
                        {children}
                    </Box>
                )}
            </div>
        );
    }


    TabPanel.propTypes = {
        children: PropTypes.node,
        value: PropTypes.number.isRequired,
        index: PropTypes.number.isRequired,
    };

    function a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            "aria-controls": `simple-tabpanel-${index}`,
        };
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Event handlers for drop down lists.
    const handleTenantOptionChange = (event) => {
        setSelectedTenantOption(event.target.value);
        fetchTenantHealthStats(event.target.value.id);
        setFocusTenantId(event.target.value.id);
    };

    const handlePeriodOptionChange = (event) => {
        setSelectedPeriodOption(event.target.value);
        setFocusPeriodId(event.target.value.id);
    };

    // Component UI
    if (loading) {
        console.log("Loading Data ....");
        return <ProgressBar message="Loading ..." loading={loading} />;
    }

    if (serviceIsDownError) {
        return (
            <>
                <ServiceIsDown />
            </>
        );
    }

    // If Teams logging is disabled for this tenant
    if (!accessDenied && selectedTenantOption && !selectedTenantOption.logTeamsAudit) {
        return (
            <Alert severity="info">
                <AlertTitle>Teams Audit Logging is not enabled for this tenant.</AlertTitle>
                If you would like this feature, please enable Preserve Teams Audit Logs under the General Audit Logs by
                editing your Tenant settings (or{" "}
                <Link onClick={() => navigate(`/Support/SupportHome`)} component="button">
                    Contact your Administrator
                </Link>
                ).
            </Alert>
        );
    }

    return (
        <>
            <CustomerPageScaffold
                breadcrumbPaths={theBreadcrumbPath}
                sectionTitle={theSectionTitle}
                sectionDescription={theSectionDesc}
                isAdminPage={isAdminPage}
                company={company}
                selectedTenant={selectedTenantOption}
                openCompanyNag={openCompanyNag}
                setOpenCompanyNag={setOpenCompanyNag}
                headerAdornment={<Box component="img" src={ImageTeams} alt="Teams Reports" sx={{ width: 75, height: 'auto' }} />}
            />

            <Box>
                <Grid container spacing={2}>
                    {accessDenied && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <AccessDenied />
                        </Grid>
                    )}

                    {!selectedTenantOption && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <Alert severity="error">
                                <AlertTitle>
                                    Your Company has not yet completed the setup process or you have not yet specified a
                                    valid Tenant.
                                </AlertTitle>
                            </Alert>
                        </Grid>
                    )}

                    {tenantHealthStats.status === 0 && (
                        <Grid item xs={12} sx={{ p: 2 }}>
                            <TenantSetupRequired />
                        </Grid>
                    )}

                    {!accessDenied &&
                        selectedTenantOption &&
                        tenantHealthStats.status >= 1 ? (
                        <>
                            <Grid item xs={12}>
                                <FilterPanel title="Dashboard filters">
                                    <PeriodDropDown
                                        selectedPeriodOption={selectedPeriodOption}
                                        handlePeriodOptionChange={handlePeriodOptionChange}
                                    />
                                </FilterPanel>
                            </Grid>
                            {/* Last updated text */}
                            <Grid item xs={12} sx={{ p: 2 }}>
                                Audit data last updated on:{" "}
                                {formatAuditJobRunTimeDateServerTimeToGMT(
                                    tenantHealthStats.teamsAuditLastSuccessfulRunTime
                                )}
                                <br />
                            </Grid>

                            {/* Row 1: Growth + All Activity (operation) + All Activity (team name) */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{ p: 2, border: "1px solid lightgray", height: "100%" }}
                                >
                                    <Box sx={{ height: "100%" }}>
                                        <AuditGrowth
                                            tenantId={focusTenantId}
                                            periodToUse={focusPeriodId}
                                            reportType="Teams"
                                        />
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Teams Activity by Operation */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 2,
                                        border: "1px solid lightgray",
                                        height: "100%",
                                        overflowWrap: "break-word",
                                    }}
                                >
                                    <TeamsAllActivity
                                        user={user}
                                        companyId={company.id}
                                        tenantId={focusTenantId}
                                        periodToUse={focusPeriodId}
                                        loadData={false}
                                        filterByColumn={"operation"}
                                    />
                                </Paper>
                            </Grid>

                            {/* Teams Activity by Team Name */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper
                                    elevation={3}
                                    sx={{ p: 2, border: "1px solid lightgray", height: "100%" }}
                                >
                                    <TeamsAllActivity
                                        user={user}
                                        companyId={company.id}
                                        tenantId={focusTenantId}
                                        periodToUse={focusPeriodId}
                                        loadData={false}
                                        filterByColumn={"teamname"}
                                    />
                                </Paper>
                            </Grid>

                            {/* Row 2: Total Audit Records (Teams) */}
                            { /* Display total sp audit record count on demand (lazy load).  Optimizes performance to show only on demand.  */}
                            <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                    <Box sx={{ height: '100%' }}>
                                        <Typography
                                            component="div"
                                            variant="h6"
                                            sx={{ fontSize: 16, pb: 2 }}
                                        >
                                            Total Audit Records
                                        </Typography>

                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            {!totalTeamsAuditRecordsLoaded && !loadingTotalTeamsAuditRecords && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={fetchTotalAuditRecordsOnDemand}
                                                >
                                                    Show total records
                                                </Button>
                                            )}

                                            {loadingTotalTeamsAuditRecords && (
                                                <Typography
                                                    component="div"
                                                    variant="body2"
                                                    sx={{ fontSize: 15 }}
                                                >
                                                    Loading count...
                                                </Typography>
                                            )}

                                            {totalTeamsAuditRecordsLoaded && !loadingTotalTeamsAuditRecords && (
                                                <TableContainer sx={{ mt: 1 }}>
                                                    <Table style={{ width: '100%', fontSize: 'inherit', fontWeight: 'inherit' }}>
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell style={{ border: 'none', padding: 2 }}>
                                                                    Teams
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    style={{ border: 'none', padding: 2 }}
                                                                >
                                                                    <NumberBadge
                                                                        number={totalTeamsAuditRecords?.toLocaleString()}
                                                                        bgcolor="#12D34A"
                                                                        txtcolor="white"
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <InsightsSearchBox colSpanVal={8} workloadName={"Teams"} />

                            {/* Row 3: Tabs for Activity / User Reports */}
                            <Grid item xs={12} sx={{ p: 2 }}>
                                <Box sx={{ width: "100%" }}>
                                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                        <Tabs
                                            value={tabValue}
                                            onChange={handleTabChange}
                                            aria-label="Teams Reports Tab"
                                        >
                                            <Tab label="Activity Reports" {...a11yProps(0)} />
                                            <Tab label="User Reports" {...a11yProps(1)} />
                                            <Tab label="Top Activity" {...a11yProps(2)} />
                                        </Tabs>
                                    </Box>
                                    <TabPanel value={tabValue} index={0}>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/TeamsAllActivityReport/${REPORT_ON_PERIOD_30DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Teams All Activity (Last 30 days)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/TeamsAllActivityReport/${REPORT_ON_PERIOD_90DAYS}/operation`
                                                )
                                            }
                                            component="button"
                                        >
                                            Teams All Activity (Last 3 months)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/TeamsAllActivityReport/${REPORT_ON_PERIOD_30DAYS}/teamname`
                                                )
                                            }
                                            component="button"
                                        >
                                            Teams All Activity by Team (Last 30 days)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/TeamsAllActivityReport/${REPORT_ON_PERIOD_90DAYS}/teamname`
                                                )
                                            }
                                            component="button"
                                        >
                                            Teams All Activity by Team (Last 3 months)
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/TeamsAllActivityByTeamAndChannelReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            All Activity by Team or Channel
                                        </Link>
                                    </TabPanel>
                                    <TabPanel value={tabValue} index={1}>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/TeamsAllActivityByUserReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            All Activity by User
                                        </Link>
                                        <Link sx={reportLinkSx}
                                            onClick={() =>
                                                navigate(
                                                    `/Reports/TeamsAllActivityByIPAddressReport`
                                                )
                                            }
                                            component="button"
                                        >
                                            All Activity by IP Address
                                        </Link>
                                    </TabPanel>
                                    <TabPanel value={tabValue} index={2}>
                                        <Link sx={reportLinkSx} onClick={() => navigate('/Reports/TopActivity/teams/users')} component="button">Top Users by Activity</Link>
                                        <Link sx={reportLinkSx} onClick={() => navigate('/Reports/TopActivity/teams/operations')} component="button">Top Operations by Activity</Link>
                                    </TabPanel>
                                </Box>
                            </Grid>
                        </>
                    ) : (
                        // Fallback "loading" state when tenant is selected but stats not yet valid (non-0 / non-loaded)
                        !accessDenied &&
                        selectedTenantOption &&
                        tenantHealthStats.status !== 0 && (
                            <Grid item xs={12} sx={{ p: 2 }}>
                                <ProgressBar message="Loading ..." loading={true} />
                            </Grid>
                        )
                    )}

                    {/* Footer row */}
                    <Grid item xs={12} sx={{ p: 2 }}>
                        <br />
                        Need a custom or new report? Provide us with your{" "}
                        <Link
                            onClick={() => navigate(`/Support/SupportHome`)}
                            component="button"
                        >
                            Product Feedback
                        </Link>
                        .
                        <br />
                        * Unless otherwise specified as UTC, graphs on page are using your browser's local timezone.
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}

export default ReportsTeams;
